(function () {
  // Order and colours must match PALETTE in ducci-viz-server/src/room-compose.js.
  const PALETTE = ['#e6194b', '#1f5fff', '#13a538', '#ff8c00', '#8e2bd9', '#00b8d4', '#f5c400', '#ff4fa3'];
  const MAX_PLACEMENTS = PALETTE.length;
  const MIN_BOX = 0.04;
  const MAX_SIDE = 1280;
  const POLL_INTERVAL_MS = 3000;
  const POLL_TIMEOUT_MS = 240000;
  const SURFACE_LABELS = { floor: 'Vloer', wall: 'Wand', both: 'Vloer + wand' };
  const LOADING_MESSAGES = [
    'We zijn gestart met je ontwerp!',
    'De producten worden in je ruimte geplaatst...',
    'Licht en schaduwen worden afgestemd...',
    'Neem gerust een koffie, dit kan even duren...',
  ];

  const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
  const round4 = (value) => Math.floor(value * 10000) / 10000;

  function formatReset(iso) {
    if (!iso) return '';
    try {
      return new Date(iso).toLocaleString('nl-NL', {
        day: 'numeric',
        month: 'long',
        hour: '2-digit',
        minute: '2-digit',
        timeZone: 'Europe/Amsterdam',
      });
    } catch (e) {
      return '';
    }
  }

  function formatNumber(value) {
    return String(Math.round(value * 10) / 10).replace('.', ',');
  }

  function errorMessage(code, data) {
    switch (code) {
      case 'login_required':
        return 'Log in om deze functie te gebruiken.';
      case 'monthly_limit_reached':
        return (
          'Je hebt deze maand alle ' +
          ((data && data.limit) || 15) +
          ' ontwerpen gebruikt. Nieuwe ontwerpen zijn weer beschikbaar op ' +
          formatReset(data && data.resetsAt) +
          '.'
        );
      case 'job_in_progress':
        return 'Er wordt al een ontwerp voor je gemaakt. Even geduld a.u.b.';
      case 'rate_limited':
        return 'Te veel verzoeken. Probeer het over enkele minuten opnieuw.';
      case 'invalid_image':
        return 'Deze foto kan niet worden gebruikt. Kies een JPG-, PNG- of WebP-foto.';
      case 'image_too_large':
        return 'De foto is te groot. Kies een kleinere foto.';
      case 'product_not_supported':
        return 'Een van de producten kan niet worden geplaatst. Verwijder het en probeer het opnieuw.';
      case 'invalid_placements':
        return 'Controleer de plaatsing: elk product mag één keer voorkomen en moet binnen de foto staan.';
      case 'surface_conflict':
        return 'Kies per vlak (vloer of wand) maar één product.';
      case 'template_unavailable':
        return 'Deze voorbeeldruimte is niet meer beschikbaar. Kies een andere ruimte.';
      case 'service_unavailable':
        return 'De planner is tijdelijk niet beschikbaar. Probeer het later opnieuw.';
      case 'failed':
        return 'Het ontwerp is mislukt. Je tegoed is niet gebruikt; probeer het opnieuw.';
      case 'expired':
        return 'Dit ontwerp is verlopen. Maak een nieuw ontwerp.';
      case 'timeout':
        return 'Het duurt langer dan verwacht. Probeer het over enkele minuten opnieuw.';
      default:
        return 'Er is iets misgegaan. Probeer het later opnieuw.';
    }
  }

  function el(tag, className, text) {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text != null) node.textContent = text;
    return node;
  }

  class RoomPlanner extends HTMLElement {
    connectedCallback() {
      this.endpoint = (this.dataset.endpoint || '/apps/ducci-viz').replace(/\/$/, '');
      this.loggedIn = this.dataset.loggedIn === 'true';
      this.quotaEl = this.querySelector('[data-rp-quota]');
      if (this.loggedIn) this.refreshQuota();
      if (!this.querySelector('[data-rp-view]')) return;

      this.scene = null;
      this.roomSizeM2 = null;
      this.templates = [];
      this.products = new Map();
      this.placements = [];
      this.busy = false;
      this.resultUrl = '';
      this.timers = {};

      this.errorEl = this.querySelector('[data-rp-error]');
      this.stage = this.querySelector('[data-rp-stage]');
      this.stageImg = this.querySelector('[data-rp-stage-img]');
      this.boxesEl = this.querySelector('[data-rp-boxes]');
      this.nextBtn = this.querySelector('[data-rp-next]');
      this.generateBtn = this.querySelector('[data-rp-generate]');
      this.errorBackBtn = this.querySelector('[data-rp-error-back]');

      this.querySelectorAll('[data-rp-products] [data-handle]').forEach((li) => {
        this.products.set(li.dataset.handle, {
          handle: li.dataset.handle,
          title: li.dataset.title,
          el: li,
          descriptor: null,
        });
      });

      this.bindEvents();
      this.loadProducts();
      this.loadTemplates();
    }

    disconnectedCallback() {
      this.stopPolling();
      this.stopProgress();
    }

    bindEvents() {
      this.querySelectorAll('[data-rp-tab]').forEach((tab) =>
        tab.addEventListener('click', () => this.showTab(tab.dataset.rpTab))
      );

      const fileInput = this.querySelector('[data-rp-file]');
      this.querySelectorAll('[data-rp-pick]').forEach((btn) => btn.addEventListener('click', () => fileInput.click()));
      fileInput.addEventListener('change', () => {
        if (fileInput.files && fileInput.files[0]) this.setUpload(fileInput.files[0]);
        fileInput.value = '';
      });

      const dropzone = this.querySelector('[data-rp-dropzone]');
      ['dragenter', 'dragover'].forEach((type) =>
        dropzone.addEventListener(type, (e) => {
          e.preventDefault();
          dropzone.classList.add('is-dragover');
        })
      );
      ['dragleave', 'drop'].forEach((type) =>
        dropzone.addEventListener(type, (e) => {
          e.preventDefault();
          dropzone.classList.remove('is-dragover');
        })
      );
      dropzone.addEventListener('drop', (e) => {
        const file = e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0];
        if (file) this.setUpload(file);
      });

      this.m2Inputs = Array.prototype.slice.call(this.querySelectorAll('[data-rp-m2]'));
      this.m2Inputs.forEach((input) =>
        input.addEventListener('input', () => {
          const value = parseFloat(String(input.value).replace(',', '.'));
          this.roomSizeM2 = value >= 1 && value <= 100 ? value : null;
          this.m2Inputs.forEach((other) => {
            if (other !== input) other.value = input.value;
          });
          this.renderTemplates();
          this.reflowBoxes();
        })
      );

      this.nextBtn.addEventListener('click', () => this.goStep(2));
      this.querySelector('[data-rp-back]').addEventListener('click', () => this.goStep(1));
      this.generateBtn.addEventListener('click', () => this.generate());
      this.querySelector('[data-rp-adjust]').addEventListener('click', () => this.goStep(2));
      this.errorBackBtn.addEventListener('click', () => this.goStep(2));
      this.querySelector('[data-rp-share]').addEventListener('click', () => this.share());
      this.querySelectorAll('[data-rp-show]').forEach((btn) =>
        btn.addEventListener('click', () => this.showResultView(btn.dataset.rpShow))
      );
    }

    /* ---------- server ---------- */

    async request(path, options) {
      const res = await fetch(this.endpoint + path, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        ...options,
      });
      let data = null;
      try {
        data = await res.json();
      } catch (e) {
        data = null;
      }
      return { status: res.status, ok: res.ok, data: data || {} };
    }

    async refreshQuota() {
      try {
        const { ok, data } = await this.request('/room/quota', { method: 'GET' });
        if (ok) this.renderQuota(data);
      } catch (e) {
        /* informational only; the server enforces the limit */
      }
    }

    renderQuota(data) {
      if (!this.quotaEl || !data || typeof data.remaining !== 'number') return;
      this.quotaEl.textContent =
        data.remaining > 0
          ? 'Nog ' + data.remaining + ' van ' + data.limit + ' badkamerontwerpen deze maand'
          : 'Maandlimiet bereikt. Weer beschikbaar op ' + formatReset(data.resetsAt);
      this.quotaEl.classList.toggle('is-empty', data.remaining <= 0);
      this.quotaEl.hidden = false;
    }

    async loadProducts() {
      const status = this.querySelector('[data-rp-products-status]');
      const handles = Array.from(this.products.keys());
      try {
        const { ok, data } = await this.request('/room/products?handles=' + encodeURIComponent(handles.join(',')), {
          method: 'GET',
        });
        if (!ok || !Array.isArray(data.products)) throw new Error(data.error || 'server_error');
        data.products.forEach((descriptor) => {
          const product = this.products.get(descriptor.handle);
          if (product) product.descriptor = descriptor;
        });
        this.products.forEach((product) => {
          if (!product.descriptor) product.descriptor = { handle: product.handle, placeable: false };
        });
        const placeable = Array.from(this.products.values()).filter((p) => p.descriptor.placeable).length;
        status.textContent = placeable
          ? 'Sleep een product naar de foto of tik op "Plaatsen". Tegels en vloeren kies je per vlak.'
          : 'Geen van deze producten kan in een badkamer worden geplaatst. Voeg bijvoorbeeld een toilet, badmeubel of tegels toe.';
      } catch (e) {
        status.textContent = 'Productgegevens konden niet worden geladen. Vernieuw de pagina om het opnieuw te proberen.';
      }
      this.renderProducts();
    }

    async loadTemplates() {
      try {
        const { ok, data } = await this.request('/room/templates', { method: 'GET' });
        this.templates = ok && Array.isArray(data.templates) ? data.templates : [];
      } catch (e) {
        this.templates = [];
      }
      this.renderTemplates();
    }

    /* ---------- step 1: scene ---------- */

    showTab(name) {
      this.querySelectorAll('[data-rp-tab]').forEach((tab) => {
        const on = tab.dataset.rpTab === name;
        tab.classList.toggle('is-active', on);
        tab.setAttribute('aria-selected', on ? 'true' : 'false');
      });
      this.querySelectorAll('[data-rp-panel]').forEach((panel) => {
        panel.hidden = panel.dataset.rpPanel !== name;
      });
    }

    setUpload(file) {
      if (!/^image\/(jpeg|png|webp)$/.test(file.type || '')) {
        this.setError('Kies een JPG-, PNG- of WebP-foto.');
        return;
      }
      if (file.size > 20 * 1024 * 1024) {
        this.setError('De foto is te groot (max. 20 MB).');
        return;
      }
      const reader = new FileReader();
      reader.onload = () =>
        this.compressImage(String(reader.result))
          .then((img) => {
            this.setScene({ type: 'upload', url: img.dataUrl, width: img.width, height: img.height });
            this.querySelector('[data-rp-upload-img]').src = img.dataUrl;
            this.querySelector('[data-rp-drop-empty]').hidden = true;
            this.querySelector('[data-rp-upload-preview]').hidden = false;
            this.setError('');
          })
          .catch(() => this.setError('De foto kon niet worden verwerkt.'));
      reader.onerror = () => this.setError('De foto kon niet worden gelezen.');
      reader.readAsDataURL(file);
    }

    compressImage(dataUrl) {
      return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => {
          const scale = Math.min(1, MAX_SIDE / Math.max(img.width, img.height));
          const width = Math.round(img.width * scale);
          const height = Math.round(img.height * scale);
          const canvas = document.createElement('canvas');
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          if (!ctx) return reject(new Error('canvas'));
          ctx.drawImage(img, 0, 0, width, height);
          resolve({ dataUrl: canvas.toDataURL('image/jpeg', 0.86), width, height });
        };
        img.onerror = reject;
        img.src = dataUrl;
      });
    }

    templateUrl(id) {
      return this.endpoint + '/room/templates/' + encodeURIComponent(id);
    }

    renderTemplates() {
      const grid = this.querySelector('[data-rp-templates]');
      const empty = this.querySelector('[data-rp-templates-empty]');
      empty.hidden = this.templates.length > 0;
      grid.textContent = '';
      if (!this.templates.length) return;

      const size = this.roomSizeM2;
      const ordered = this.templates.slice();
      if (size) ordered.sort((a, b) => Math.abs(a.areaM2 - size) - Math.abs(b.areaM2 - size));
      const recommended = size ? new Set(ordered.slice(0, 3).map((t) => t.id)) : new Set();

      ordered.forEach((template) => {
        const selected = this.scene && this.scene.type === 'template' && this.scene.id === template.id;
        const card = el('button', 'room-planner__template' + (selected ? ' is-selected' : ''));
        card.type = 'button';
        card.setAttribute('role', 'radio');
        card.setAttribute('aria-checked', selected ? 'true' : 'false');
        const img = el('img');
        img.src = this.templateUrl(template.id);
        img.alt = template.label;
        img.loading = 'lazy';
        img.width = 400;
        img.height = 300;
        card.appendChild(img);
        if (recommended.has(template.id)) card.appendChild(el('span', 'room-planner__badge', 'Aanbevolen'));
        card.appendChild(el('span', 'room-planner__template-title', template.label));
        card.appendChild(
          el(
            'span',
            'room-planner__template-meta',
            formatNumber(template.widthM) + ' × ' + formatNumber(template.depthM) + ' m · ' + formatNumber(template.areaM2) + ' m²'
          )
        );
        card.addEventListener('click', () => {
          this.setScene({ type: 'template', id: template.id, url: this.templateUrl(template.id), width: 4, height: 3 });
          this.renderTemplates();
        });
        grid.appendChild(card);
      });
    }

    setScene(scene) {
      this.scene = scene;
      this.nextBtn.disabled = false;
      this.reflowBoxes();
    }

    roomReference() {
      if (this.scene && this.scene.type === 'template') {
        const template = this.templates.find((item) => item.id === this.scene.id);
        if (template) return { widthM: template.widthM, ceilingM: template.ceilingM || 2.6 };
      }
      return { widthM: this.roomSizeM2 ? Math.sqrt(this.roomSizeM2) : 2.2, ceilingM: 2.6 };
    }

    boxFromFace(face, centerX, centerY) {
      const room = this.roomReference();
      const w = clamp((face.widthMm / 1000 / room.widthM) * 0.62, 0.03, 0.7);
      const h = clamp((face.heightMm / 1000 / room.ceilingM) * 0.58, 0.03, 0.75);
      return { x: clamp(centerX - w / 2, 0, 1 - w), y: clamp(centerY - h / 2, 0, 1 - h), w, h };
    }

    reflowBoxes() {
      let moved = false;
      this.placements.forEach((placement) => {
        if (!placement.box) return;
        const product = this.products.get(placement.handle);
        const face = product && product.descriptor && product.descriptor.face;
        if (!face) return;
        placement.box = this.boxFromFace(face, placement.box.x + placement.box.w / 2, placement.box.y + placement.box.h / 2);
        moved = true;
      });
      if (moved && this.boxesEl) this.renderBoxes();
    }

    /* ---------- step 2: placement ---------- */

    goStep(step) {
      if (step === 2 && !this.scene) return;
      if (step === 2 && this.stageImg.getAttribute('src') !== this.scene.url) {
        this.stageImg.src = this.scene.url;
      }
      this.querySelectorAll('[data-rp-view]').forEach((view) => {
        view.hidden = view.dataset.rpView !== String(step);
      });
      this.querySelectorAll('[data-rp-dot]').forEach((dot) => {
        dot.classList.toggle('is-active', Number(dot.dataset.rpDot) <= step);
      });
      if (step !== 3) {
        this.setError('');
        this.errorBackBtn.hidden = true;
      }
      if (step === 2) this.renderBoxes();
      this.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    placementFor(handle) {
      return this.placements.find((p) => p.handle === handle) || null;
    }

    surfaceTaken(target, exceptHandle) {
      return this.placements.some(
        (p) => p.surface && p.handle !== exceptHandle && (p.surface === target || p.surface === 'both' || target === 'both')
      );
    }

    addPlacement(placement) {
      if (this.placements.length >= MAX_PLACEMENTS) {
        this.setError('Je kunt maximaal ' + MAX_PLACEMENTS + ' producten tegelijk plaatsen.');
        return;
      }
      this.placements.push(placement);
      this.setError('');
      this.changed();
    }

    removePlacement(handle) {
      this.placements = this.placements.filter((p) => p.handle !== handle);
      this.changed();
    }

    changed() {
      this.renderBoxes();
      this.renderProducts();
      this.generateBtn.disabled = this.placements.length === 0 || this.busy;
    }

    defaultBox(product, centerX, centerY) {
      const face = product.descriptor && product.descriptor.face;
      if (face && face.widthMm && face.heightMm) return this.boxFromFace(face, centerX, centerY);
      const size = (product.descriptor && product.descriptor.box) || { w: 0.2, h: 0.25 };
      const w = clamp(size.w, MIN_BOX, 1);
      const h = clamp(size.h, MIN_BOX, 1);
      return { x: clamp(centerX - w / 2, 0, 1 - w), y: clamp(centerY - h / 2, 0, 1 - h), w, h };
    }

    renderProducts() {
      this.products.forEach((product) => {
        const li = product.el;
        const meta = li.querySelector('[data-rp-meta]');
        const controls = li.querySelector('[data-rp-controls]');
        const descriptor = product.descriptor;
        li.classList.toggle('is-loading', !descriptor);
        controls.textContent = '';
        li.style.removeProperty('--rp-color');
        li.classList.remove('is-placed');
        if (!descriptor) return;

        if (!descriptor.placeable) {
          li.classList.add('is-disabled');
          meta.textContent = 'Niet plaatsbaar';
          return;
        }

        const index = this.placements.findIndex((p) => p.handle === product.handle);
        const placement = index >= 0 ? this.placements[index] : null;
        meta.textContent = descriptor.label || '';
        if (placement) {
          li.classList.add('is-placed');
          li.style.setProperty('--rp-color', PALETTE[index]);
          const badge = el('span', 'room-planner__num', String(index + 1));
          badge.style.background = PALETTE[index];
          meta.prepend(badge);
        }
        const full = !placement && this.placements.length >= MAX_PLACEMENTS;

        if (descriptor.mode === 'surface') {
          const group = el('span', 'room-planner__surfaces');
          group.setAttribute('role', 'group');
          group.setAttribute('aria-label', 'Waar toepassen voor ' + product.title);
          descriptor.surfaces.forEach((surface) => {
            const on = placement && placement.surface === surface;
            const btn = el('button', 'room-planner__chip' + (on ? ' is-selected' : ''), SURFACE_LABELS[surface] || surface);
            btn.type = 'button';
            btn.setAttribute('aria-pressed', on ? 'true' : 'false');
            btn.disabled = !on && (full || this.surfaceTaken(surface, product.handle));
            btn.addEventListener('click', () => {
              if (on) return this.removePlacement(product.handle);
              if (placement) {
                placement.surface = surface;
                this.changed();
              } else {
                this.addPlacement({ handle: product.handle, surface });
              }
            });
            group.appendChild(btn);
          });
          controls.appendChild(group);
          return;
        }

        if (placement) {
          const remove = el('button', 'room-planner__link', 'Verwijderen');
          remove.type = 'button';
          remove.addEventListener('click', () => this.removePlacement(product.handle));
          controls.appendChild(remove);
          return;
        }

        const drag = el('button', 'room-planner__drag', '⠿ Slepen');
        drag.type = 'button';
        drag.disabled = full;
        drag.setAttribute('aria-label', product.title + ' naar de foto slepen');
        drag.addEventListener('pointerdown', (e) => this.startListDrag(e, product));
        drag.addEventListener('click', (e) => e.preventDefault());
        const place = el('button', 'room-planner__chip', 'Plaatsen');
        place.type = 'button';
        place.disabled = full;
        place.addEventListener('click', () => {
          const offset = (this.placements.length % 4) * 0.06;
          this.addPlacement({ handle: product.handle, box: this.defaultBox(product, 0.35 + offset, 0.55) });
        });
        controls.append(drag, place);
      });
    }

    startListDrag(event, product) {
      if (event.button !== 0 || this.placements.length >= MAX_PLACEMENTS) return;
      event.preventDefault();
      const ghost = el('div', 'room-planner__ghost', product.title);
      ghost.style.setProperty('--rp-color', PALETTE[this.placements.length]);
      document.body.appendChild(ghost);
      const move = (e) => {
        ghost.style.transform = 'translate(' + e.clientX + 'px, ' + e.clientY + 'px)';
        const rect = this.stageImg.getBoundingClientRect();
        const inside = e.clientX >= rect.left && e.clientX <= rect.right && e.clientY >= rect.top && e.clientY <= rect.bottom;
        this.stage.classList.toggle('is-target', inside);
      };
      const end = (e) => {
        window.removeEventListener('pointermove', move);
        window.removeEventListener('pointerup', end);
        window.removeEventListener('pointercancel', end);
        ghost.remove();
        this.stage.classList.remove('is-target');
        if (e.type !== 'pointerup') return;
        const rect = this.stageImg.getBoundingClientRect();
        if (e.clientX < rect.left || e.clientX > rect.right || e.clientY < rect.top || e.clientY > rect.bottom) return;
        const cx = (e.clientX - rect.left) / rect.width;
        const cy = (e.clientY - rect.top) / rect.height;
        this.addPlacement({ handle: product.handle, box: this.defaultBox(product, cx, cy) });
      };
      move(event);
      window.addEventListener('pointermove', move);
      window.addEventListener('pointerup', end);
      window.addEventListener('pointercancel', end);
    }

    renderBoxes() {
      this.boxesEl.textContent = '';
      this.placements.forEach((placement, index) => {
        if (!placement.box) return;
        const product = this.products.get(placement.handle);
        const box = el('div', 'room-planner__box');
        box.style.setProperty('--rp-color', PALETTE[index]);
        box.tabIndex = 0;
        box.setAttribute('role', 'group');
        box.setAttribute(
          'aria-label',
          (index + 1) + ': ' + (product ? product.title : placement.handle) + '. Pijltjestoetsen verplaatsen, Delete verwijdert. De grootte volgt de productafmetingen.'
        );
        const label = el('span', 'room-planner__box-label');
        label.appendChild(el('span', 'room-planner__box-num', String(index + 1)));
        label.appendChild(el('span', 'room-planner__box-title', product ? product.title : placement.handle));
        const remove = el('button', 'room-planner__box-remove', '×');
        remove.type = 'button';
        remove.setAttribute('aria-label', 'Verwijderen');
        remove.addEventListener('click', () => this.removePlacement(placement.handle));
        remove.addEventListener('pointerdown', (e) => e.stopPropagation());
        box.append(label, remove);
        this.positionBox(box, placement.box);

        box.addEventListener('pointerdown', (e) => this.startBoxDrag(e, box, placement));
        box.addEventListener('keydown', (e) => this.onBoxKey(e, box, placement));
        this.boxesEl.appendChild(box);
      });
    }

    positionBox(node, b) {
      node.style.left = b.x * 100 + '%';
      node.style.top = b.y * 100 + '%';
      node.style.width = b.w * 100 + '%';
      node.style.height = b.h * 100 + '%';
    }

    startBoxDrag(event, node, placement) {
      if (event.button !== 0) return;
      event.preventDefault();
      try {
        node.setPointerCapture(event.pointerId);
      } catch (e) {
        /* capture is best effort; moves still arrive while the pointer stays on the box */
      }
      node.classList.add('is-dragging');
      const rect = this.stageImg.getBoundingClientRect();
      const start = Object.assign({}, placement.box);
      const sx = event.clientX;
      const sy = event.clientY;
      const move = (e) => {
        const dx = (e.clientX - sx) / rect.width;
        const dy = (e.clientY - sy) / rect.height;
        const b = placement.box;
        b.x = clamp(start.x + dx, 0, 1 - start.w);
        b.y = clamp(start.y + dy, 0, 1 - start.h);
        this.positionBox(node, b);
      };
      const end = () => {
        node.classList.remove('is-dragging');
        node.removeEventListener('pointermove', move);
        node.removeEventListener('pointerup', end);
        node.removeEventListener('pointercancel', end);
      };
      node.addEventListener('pointermove', move);
      node.addEventListener('pointerup', end);
      node.addEventListener('pointercancel', end);
    }

    onBoxKey(event, node, placement) {
      const b = placement.box;
      const step = 0.01;
      const keys = { ArrowLeft: [-step, 0], ArrowRight: [step, 0], ArrowUp: [0, -step], ArrowDown: [0, step] };
      if (event.key === 'Delete' || event.key === 'Backspace') {
        event.preventDefault();
        this.removePlacement(placement.handle);
        return;
      }
      const delta = keys[event.key];
      if (!delta) return;
      event.preventDefault();
      b.x = clamp(b.x + delta[0], 0, 1 - b.w);
      b.y = clamp(b.y + delta[1], 0, 1 - b.h);
      this.positionBox(node, b);
    }

    /* ---------- step 3: generate ---------- */

    async generate() {
      if (this.busy || !this.scene || !this.placements.length) return;
      this.busy = true;
      this.generateBtn.disabled = true;
      this.resultUrl = '';
      this.goStep(3);
      this.querySelector('[data-rp-loading]').hidden = false;
      this.querySelector('[data-rp-result]').hidden = true;
      this.startProgress();

      const scene =
        this.scene.type === 'upload'
          ? { type: 'upload', image: this.scene.url, width: this.scene.width, height: this.scene.height }
          : { type: 'template', id: this.scene.id };
      const placements = this.placements.map((p) =>
        p.surface
          ? { handle: p.handle, surface: p.surface }
          : { handle: p.handle, box: { x: round4(p.box.x), y: round4(p.box.y), w: round4(p.box.w), h: round4(p.box.h) } }
      );

      try {
        const { status, data } = await this.request('/room/generate', {
          method: 'POST',
          body: JSON.stringify({ scene, roomSizeM2: this.roomSizeM2, placements }),
        });
        this.renderQuota(data);
        if (status !== 202 || !data.jobId) return this.fail(data.error || 'server_error', data);
        this.poll(data.jobId, Date.now());
      } catch (e) {
        this.fail('network');
      }
    }

    poll(jobId, startedAt) {
      this.stopPolling();
      this.timers.poll = window.setTimeout(async () => {
        if (Date.now() - startedAt > POLL_TIMEOUT_MS) return this.fail('timeout');
        try {
          const { ok, data } = await this.request('/jobs/' + encodeURIComponent(jobId), { method: 'GET' });
          if (!ok) return this.fail(data.error || 'server_error', data);
          this.renderQuota(data);
          if (data.status === 'pending') return this.poll(jobId, startedAt);
          if (data.status === 'done' && data.image) return this.showResult(data.image);
          this.fail(data.status || 'server_error', data);
        } catch (e) {
          this.poll(jobId, startedAt);
        }
      }, POLL_INTERVAL_MS);
    }

    stopPolling() {
      if (this.timers && this.timers.poll) window.clearTimeout(this.timers.poll);
      if (this.timers) this.timers.poll = null;
    }

    startProgress() {
      this.stopProgress();
      const bar = this.querySelector('[data-rp-progress]');
      const msg = this.querySelector('[data-rp-loading-msg]');
      let pct = 4;
      let index = 0;
      bar.style.width = pct + '%';
      msg.textContent = LOADING_MESSAGES[0];
      this.timers.progress = window.setInterval(() => {
        pct = Math.min(94, pct + Math.random() * 3);
        bar.style.width = pct + '%';
      }, 1500);
      this.timers.msg = window.setInterval(() => {
        index = (index + 1) % LOADING_MESSAGES.length;
        msg.textContent = LOADING_MESSAGES[index];
      }, 9000);
    }

    stopProgress() {
      if (!this.timers) return;
      window.clearInterval(this.timers.progress);
      window.clearInterval(this.timers.msg);
      this.timers.progress = this.timers.msg = null;
    }

    fail(code, data) {
      this.busy = false;
      this.stopPolling();
      this.stopProgress();
      this.generateBtn.disabled = this.placements.length === 0;
      this.querySelector('[data-rp-loading]').hidden = true;
      this.errorBackBtn.hidden = false;
      this.setError(errorMessage(code, data));
      if (code === 'monthly_limit_reached' || code === 'failed') this.refreshQuota();
    }

    showResult(url) {
      this.busy = false;
      this.stopProgress();
      this.resultUrl = url;
      this.generateBtn.disabled = false;
      this.querySelector('[data-rp-loading]').hidden = true;
      this.querySelector('[data-rp-result]').hidden = false;
      this.querySelector('[data-rp-result-img]').src = url;
      this.querySelector('[data-rp-original-img]').src = this.scene.url;
      this.querySelector('[data-rp-download]').href = url;
      this.showResultView('generated');
    }

    showResultView(mode) {
      const generated = mode === 'generated';
      this.querySelector('[data-rp-result-img]').hidden = !generated;
      this.querySelector('[data-rp-original-img]').hidden = generated;
      this.querySelectorAll('[data-rp-show]').forEach((btn) => {
        const on = btn.dataset.rpShow === mode;
        btn.classList.toggle('is-active', on);
        btn.setAttribute('aria-pressed', on ? 'true' : 'false');
      });
    }

    async share() {
      if (!this.resultUrl) return;
      try {
        const blob = await (await fetch(this.resultUrl)).blob();
        const file = new File([blob], 'badkamer-ontwerp.jpg', { type: blob.type || 'image/jpeg' });
        if (navigator.canShare && navigator.canShare({ files: [file] })) {
          await navigator.share({ title: 'Mijn badkamerontwerp', text: 'Zo wordt mijn nieuwe badkamer', files: [file] });
          return;
        }
        this.querySelector('[data-rp-download]').click();
      } catch (e) {
        /* user cancelled */
      }
    }

    setError(message) {
      if (!this.errorEl) return;
      this.errorEl.textContent = message || '';
      this.errorEl.hidden = !message;
    }
  }

  if (!customElements.get('room-planner')) customElements.define('room-planner', RoomPlanner);
})();
